/** Catalogues tab - which catalogues this supplier participates in. Read-only summary;
 * links out to the Procurement Catalogues screen for full catalogue management. Demo Mode
 * only, local-only mock data - see `supplierProfileData.ts`. */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Section } from '../../../../../components/ui/Section';
import { getSupplierCatalogues } from '../../../../../mocks/data/supplierProfileData';

export interface CataloguesTabProps {
  supplierId: number;
}

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  Active: 'success',
  Draft: 'default',
  PendingApproval: 'warning',
  Expired: 'error',
};

const CataloguesTab: React.FC<CataloguesTabProps> = ({ supplierId }) => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const catalogues = useMemo(() => getSupplierCatalogues(supplierId), [supplierId]);

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 3 }}>{t('supplierCatalogues', 'Catalogues')}</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('catalogueName', 'Catalogue name')}</TableCell>
              <TableCell>{t('catalogueType', 'Catalogue type')}</TableCell>
              <TableCell>{t('status', 'Status')}</TableCell>
              <TableCell>{t('validFrom', 'Valid from')}</TableCell>
              <TableCell>{t('validTo', 'Valid to')}</TableCell>
              <TableCell>{t('itemCount', 'Item count')}</TableCell>
              <TableCell>{t('assignedSites', 'Assigned sites')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {catalogues.length === 0 && (
              <TableRow><TableCell colSpan={7}>{t('noCatalogues', 'No catalogues assigned to this supplier yet.')}</TableCell></TableRow>
            )}
            {catalogues.map((catalogue) => (
              <TableRow
                key={catalogue.id}
                hover
                onClick={() => navigate(`/procurement/catalogues?searchTerm=${encodeURIComponent(catalogue.catalogueName)}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{catalogue.catalogueName}</TableCell>
                <TableCell>{catalogue.catalogueType}</TableCell>
                <TableCell><Chip label={catalogue.status}
                  size="small"
                  color={STATUS_COLOR[catalogue.status]} /></TableCell>
                <TableCell>{catalogue.validFrom}</TableCell>
                <TableCell>{catalogue.validTo ?? '-'}</TableCell>
                <TableCell>{catalogue.itemCount}</TableCell>
                <TableCell>{catalogue.assignedSites}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
};

export default CataloguesTab;
